import React, { useCallback } from 'react'
import useModal, { ModalOnCencel, ModalOnConfrim } from '@hooks/useModal'
import { Message } from '@utils/message'
import { FieldValues, useForm } from 'react-hook-form'
import { useDropzone } from 'react-dropzone'
import 'twin.macro'
import { useMutation } from 'react-query'
import { api } from '@api/index'
import objToFormdata from '@utils/objToFormdata'
import MessageModal from '@components/shared/MessageModal'

interface Props {
  onConfirm?: ModalOnConfrim
  onCencel?: ModalOnCencel
}

const AddFileModal = ({ onConfirm, onCencel }: Props) => {
  const [openResModal] = useModal()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: 'onChange' })

  const addFileMutation = useMutation(
    (formData: FormData) => api.mediaService.createMedia(formData),
    {
      onError: async () => {
        const res = await openResModal(
          <MessageModal>{Message.error.FAIL_ADD_FILE}</MessageModal>
        )
        if (res) {
          onConfirm?.(false, 200)
        }
      },
      onSuccess: async () => {
        const res = await openResModal(
          <MessageModal>{Message.info.SUCCESS_ADD_FILE}</MessageModal>
        )
        if (res) {
          onConfirm?.(true, 200)
        }
      },
    }
  )

  const onDrop = useCallback((acceptedFiles) => {
    const fileSize = Math.floor(acceptedFiles[0].size / 1024 / 1024)
    if (fileSize < 300) {
      setValue('file', acceptedFiles[0])
      return
    }
    openResModal(
      <MessageModal>{Message.error.FAIL_ADD_FILE_BECAUSE_SIZE}</MessageModal>
    )
  }, [])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
  })

  const handleSubmitAddFile = (form: FieldValues) => {
    const formData = objToFormdata(form)
    addFileMutation.mutate(formData)
  }

  const getCurrentFileSize = () => {
    const file = watch('file')
    if (!file) return 0
    return (file.size / 1024 / 1024).toFixed(2)
  }

  return (
    <>
      <div className="px-20 py-25 max-w-600 modal">
        <form onSubmit={handleSubmit(handleSubmitAddFile)}>
          <div className="justify-start text-xl font-bold mb-25">
            PDF 파일 추가
          </div>
          <div className="flex flex-col w-full gap-12 mb-3">
            <label className="text-base font-bold text-gray-500">제목</label>
            <input
              {...register('title', { required: Message.valid.REQUIRE_TITLE })}
              type="text"
              className="input"
              tw="h-36"
            />
            <div className="flex items-center h-10 py-1 text-base text-red-500">
              {errors?.title?.message}
            </div>
            <label className="text-base font-bold text-gray-500">
              파일 첨부
            </label>
            <div
              {...getRootProps()}
              className="flex flex-col items-center justify-center w-full mb-4 border-2 border-solid outline-none cursor-pointer all:text-base all:text-gray-500 min-h-150 border-neutral-400"
            >
              <input
                {...register('file', { required: Message.valid.REQUIRE_FILE })}
                className="w-0 h-0"
              />
              <input {...getInputProps()} className="w-0 h-0" />

              {watch('file') ? (
                <div tw="text-pink-500">선택된 파일 : {watch('file').name}</div>
              ) : (
                <div tw="text-red-500">{errors?.file?.message}</div>
              )}
              <div>이곳에 파일을 드래그하세요.</div>
              <div>
                또는 파일 선택 {`( ${getCurrentFileSize()} MB / 300 MB )`}
              </div>
            </div>
          </div>
          <div className="flex justify-end w-full gap-8 text-base font-bold mt-15">
            <button
              type="button"
              onClick={onConfirm}
              className="px-20 py-6 button"
            >
              취소
            </button>
            <button
              type="submit"
              onClick={onCencel}
              className="px-20 py-6 button"
            >
              추가
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default AddFileModal
